/**
 * BookInspectionModal Component
 *
 * Modern, minimalistic modal for creating new FleetHub inspection orders.
 * Apple-inspired design with date/time pickers and follow-up checkbox.
 *
 * @module components/BookInspectionModal
 */

import { FC, useState, useEffect, useRef, useMemo, useCallback } from "react"
import {
  Modal,
  View,
  ViewStyle,
  TextStyle,
  // eslint-disable-next-line no-restricted-imports -- multiline textarea needs base TextInput; TextField wrapper doesn't support naked card layout
  TextInput,
  TouchableOpacity,
  Pressable,
  Platform,
  ScrollView,
  Animated,
  ActivityIndicator,
  Linking,
  useWindowDimensions,
  type NativeSyntheticEvent,
  type NativeScrollEvent,
} from "react-native"
import * as Sentry from "@sentry/react"
import dayjs from "dayjs"
import Icon from "react-native-vector-icons/MaterialIcons"

import "dayjs/locale/de"
import { DateTimeSelector } from "@/components/DateTimeSelector"
import {
  DEFAULT_INSPECTION_TYPE,
  INSPECTION_SERVICES,
  getInspectionService,
  type InspectionType,
} from "@/components/inspection-catalog"
import { NumberPlateInput } from "@/components/NumberPlateInput"
import { Text } from "@/components/Text"
import { TextField } from "@/components/TextField"
import { uploadBookingDocumentsB2b, BookingDocumentError } from "@/services/bookingDocuments"
import {
  getCustomerExpansionRegion,
  getLocationOpeningHours,
  getOptimalBookingTimesActive,
  getCustomerCompanies,
  createFleetHubOrder,
  type OpeningHoursResponse,
  type OptimalBookingTimesResponse,
  type WeeklySchedule,
} from "@/services/firebase/functions"
import { useFleetHubStore } from "@/stores/useFleetHubStore"
import { colors, spacing, breakpoints } from "@/theme"
import { isDateDisabled } from "@/utils/dateTimeUtils"
import { parsePlateIntoParts, formatPartsForHubSpot, validatePlate } from "@/utils/plateUtils"
import { isHbsDisabledAtHub } from "@/utils/serviceTypeAvailability"

// Configure dayjs to use German locale
dayjs.locale("de")

export interface BookInspectionModalProps {
  /**
   * Whether the modal is visible
   */
  visible: boolean

  /**
   * Callback when modal is closed
   */
  onClose: () => void

  /**
   * Whether an order is currently being created
   */
  isBookingOrder?: boolean

  /**
   * Error message from booking attempt
   */
  bookingError?: string | null
}

/**
 * Get default date (today, or next Monday if today is Sunday) in YYYY-MM-DD format
 */
const _getDefaultDate = (): string => {
  let date = dayjs()

  // If today is Sunday, default to tomorrow (Monday)
  if (date.day() === 0) {
    date = date.add(1, "day")
  }

  return date.format("YYYY-MM-DD")
}

/**
 * Get default time (2 hours from now, max 17:00) in HH:MM format
 */
const _getDefaultTime = (): string => {
  const now = new Date()
  const twoHoursLater = new Date(now.getTime() + 2 * 60 * 60 * 1000)

  let hours = twoHoursLater.getHours()
  const minutes = twoHoursLater.getMinutes()

  // Cap at 17:00
  if (hours > 17 || (hours === 17 && minutes > 0)) {
    hours = 17
    return "17:00"
  }

  return `${String(hours).padStart(2, "0")}:${String(minutes).padStart(2, "0")}`
}

/**
 * Format time for display (e.g., "14:30" -> "14:30 Uhr")
 */
const formatTime = (time: string): string => {
  if (!time) return ""
  return `${time} Uhr`
}

/**
 * Wizard Progress Bar Component
 */
interface ProgressBarProps {
  currentStep: number
  totalSteps: number
  onBack?: () => void
  showBackButton?: boolean
}

const WizardProgressBar: FC<ProgressBarProps> = ({
  currentStep,
  totalSteps,
  onBack,
  showBackButton,
}) => {
  return (
    <View style={$progressBarContainer}>
      <View style={$progressBarWithBack}>
        {/* Back button - only show if not on first step */}
        {showBackButton && onBack && (
          <TouchableOpacity onPress={onBack} style={$backIconButton}>
            <Icon name="arrow-back" size={24} color={colors.text} />
          </TouchableOpacity>
        )}

        {/* Progress steps */}
        <View style={$progressBarTrack}>
          {Array.from({ length: totalSteps }).map((_, index) => {
            const stepNumber = index + 1
            const isCompleted = stepNumber < currentStep
            const isCurrent = stepNumber === currentStep

            return (
              <View key={stepNumber} style={$progressStepWrapper}>
                <View
                  style={[
                    $progressStepIndicator,
                    isCompleted && $progressStepCompleted,
                    isCurrent && $progressStepCurrent,
                  ]}
                >
                  {isCompleted ? (
                    <Icon name="check" size={16} color={colors.surface} />
                  ) : (
                    <Text style={[$progressStepNumber, isCurrent && $progressStepNumberCurrent]}>
                      {stepNumber}
                    </Text>
                  )}
                </View>
                {index < totalSteps - 1 && <View style={$progressConnector} />}
              </View>
            )
          })}
        </View>
      </View>
    </View>
  )
}

/**
 * Book Inspection Modal
 *
 * Allows users to create new inspection orders with:
 * - Pickup date (Abholdatum)
 * - Pickup time (Spateste Abholung)
 * - Follow-up inspection checkbox (Nachkontrolle)
 */
export const BookInspectionModal: FC<BookInspectionModalProps> = ({
  visible,
  onClose,
  isBookingOrder = false,
  bookingError = null,
}) => {
  // Window dimensions for responsive design
  const { width } = useWindowDimensions()
  const _isMobile = width < breakpoints.mobile

  // Store hooks
  const createFleetOrder = useFleetHubStore((state) => state.createFleetOrder)
  const startPostBookingFlow = useFleetHubStore((state) => state.startPostBookingFlow)
  const setPendingOrderDraft = useFleetHubStore((state) => state.setPendingOrderDraft)
  const bookingNachkontrollePreselected = useFleetHubStore(
    (state) => state.bookingNachkontrollePreselected,
  )

  // Wizard step management
  const [currentStep, setCurrentStep] = useState<number>(1)

  // Scroll hint arrow (refs only — effects after stepMapping)
  const [showScrollHint, setShowScrollHint] = useState(false)
  const scrollHintOpacity = useRef(new Animated.Value(0)).current
  const scrollHintBounce = useRef(new Animated.Value(0)).current
  const scrollViewRef = useRef<ScrollView>(null)
  const scrollHintDismissed = useRef(false)
  const timesCacheRef = useRef<Record<string, OptimalBookingTimesResponse>>({})
  const preloadingRef = useRef<Set<string>>(new Set())

  // Local state with defaults
  const [selectedDate, setSelectedDate] = useState<string>("")
  const [loadingCompanies, setLoadingCompanies] = useState<boolean>(true)
  const [selectedTime, setSelectedTime] = useState<string>("")
  const [inspectionType, setInspectionType] = useState<InspectionType>(DEFAULT_INSPECTION_TYPE)
  // Derived from inspectionType; kept as legacy field for the backend payload.
  const nachkontrolle = inspectionType === "nachkontrolle"
  const [serviceDropdownOpen, setServiceDropdownOpen] = useState<boolean>(false)
  const serviceDropdownRef = useRef<View>(null)
  const [walkIn, setWalkIn] = useState<boolean>(false) // Self-drive to inspection center (no pickup/delivery)
  const [serviceType, setServiceType] = useState<"pickup_delivery" | "driveby">("pickup_delivery")
  const [includeNumberPlate, setIncludeNumberPlate] = useState<boolean>(false)
  const [numberPlate, setNumberPlate] = useState<string>("")

  // ── Nachricht state (PLA-151) ──────────────────────────────────────────────
  // Toggle is on/off; typed text is retained while the toggle is collapsed within
  // the same modal session (matches Kennzeichen behaviour). On modal reopen the
  // defense-in-depth reset effect (below) clears both toggle + text.
  const [includeNachricht, setIncludeNachricht] = useState<boolean>(false)
  const [nachrichtText, setNachrichtText] = useState<string>("")
  const NACHRICHT_MAX_CHARS = 500
  // ── End Nachricht state ────────────────────────────────────────────────────

  // ── Document upload state (PLA-122) ────────────────────────────────────────
  const [includeDocuments, setIncludeDocuments] = useState<boolean>(false)
  const [documentFiles, setDocumentFiles] = useState<File[]>([])
  const [documentValidationError, setDocumentValidationError] = useState<string | null>(null)
  /** null = no attach attempted; false = in progress; true = succeeded */
  const [attachSucceeded, setAttachSucceeded] = useState<boolean | null>(null)
  const [attachError, setAttachError] = useState<string | null>(null)
  const [isAttaching, setIsAttaching] = useState<boolean>(false)
  // Local booking-create error for the documents-path submit. The standard
  // (no-docs) path surfaces errors via the store-driven `bookingError` prop;
  // the docs path bypasses the store and would otherwise silently swallow
  // booking creation failures. See Codex P2 on PR #132.
  const [localBookingError, setLocalBookingError] = useState<string | null>(null)
  /** ticketId captured after createFleetHubOrder, held for the retry flow */
  const pendingTicketIdRef = useRef<string | null>(null)
  // ── End document upload state ──────────────────────────────────────────────

  // Bumped at the start of every opening-hours/optimal-times fetch below so a
  // response from a superseded company/date selection can detect it is stale
  // and skip its setState calls instead of clobbering the current selection's
  // state (e.g. re-applying company A's HBS gate after the user has already
  // switched to company B). See Codex P2 on PR #192.
  const fetchRequestIdRef = useRef(0)
  // The 20s optimal-times watchdog of the run that currently owns one. Held
  // outside the effect so the cleanup can kill it when a newer selection takes
  // over — otherwise a superseded run's timer would raise `fetchTimeout` for
  // the new selection, and DateTimeSelector hides every slot while that is set.
  const optimalTimesTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  const [openingHours, setOpeningHours] = useState<OpeningHoursResponse | null>(null)
  /**
   * True when the hub resolved from the selected company's `expansionRegion`
   * does not offer Hol- und Bring-Service (ADR-0051). Derived from the opening-
   * hours response, which the fetch effect below re-runs on open and on every
   * company change — so single- and multi-company accounts are both covered
   * without an extra call. Absent field = offered.
   */
  const hbsDisabledAtHub = isHbsDisabledAtHub(openingHours)
  const [loadingHours, setLoadingHours] = useState<boolean>(false)
  const [optimalTimes, setOptimalTimes] = useState<OptimalBookingTimesResponse | null>(null)
  const [loadingOptimalTimes, setLoadingOptimalTimes] = useState<boolean>(false)
  const [fetchTimeout, setFetchTimeout] = useState<boolean>(false)
  const [_expansionRegion, setExpansionRegion] = useState<string | null>(null)
  const [availableCompanies, setAvailableCompanies] = useState<
    Array<{ id: string; name: string; expansionRegion?: string; estimatedLegMinutes?: number }>
  >([])
  const [selectedCompanyId, setSelectedCompanyId] = useState<string | null>(null)
  const [companySearchQuery, setCompanySearchQuery] = useState<string>("")

  const filteredCompanies = useMemo(() => {
    if (!companySearchQuery.trim()) return availableCompanies

    const query = companySearchQuery.toLowerCase()
    return availableCompanies.filter((company) => company.name.toLowerCase().includes(query))
  }, [availableCompanies, companySearchQuery])

  const hasMultipleCompanies = availableCompanies.length > 1

  // Step mapping - dynamic based on company count (date+time merged into one step)
  const getStepMapping = useCallback(() => {
    if (hasMultipleCompanies) {
      return {
        company: 1 as number | null,
        dateTime: 2,
        summary: 3,
        total: 3,
      }
    }
    return {
      company: null as number | null,
      dateTime: 1,
      summary: 2,
      total: 2,
    }
  }, [hasMultipleCompanies])

  const stepMapping = getStepMapping()

  // Scroll hint effects (must be after stepMapping)
  useEffect(() => {
    if (currentStep === stepMapping.dateTime && !scrollHintDismissed.current) {
      const timer = setTimeout(() => {
        if (!scrollHintDismissed.current) {
          setShowScrollHint(true)
          Animated.timing(scrollHintOpacity, {
            toValue: 1,
            duration: 400,
            useNativeDriver: true,
          }).start()
          Animated.loop(
            Animated.sequence([
              Animated.timing(scrollHintBounce, {
                toValue: 6,
                duration: 600,
                useNativeDriver: true,
              }),
              Animated.timing(scrollHintBounce, {
                toValue: 0,
                duration: 600,
                useNativeDriver: true,
              }),
            ]),
          ).start()
        }
      }, 2500)
      return () => clearTimeout(timer)
    } else {
      setShowScrollHint(false)
      scrollHintOpacity.setValue(0)
    }
    return undefined
  }, [currentStep, stepMapping.dateTime, scrollHintOpacity, scrollHintBounce])

  useEffect(() => {
    if (visible) {
      scrollHintDismissed.current = false
    }
  }, [visible])

  const dismissScrollHint = useCallback(() => {
    scrollHintDismissed.current = true
    Animated.timing(scrollHintOpacity, {
      toValue: 0,
      duration: 200,
      useNativeDriver: true,
    }).start(() => setShowScrollHint(false))
  }, [scrollHintOpacity])

  const handleScrollHintPress = useCallback(() => {
    scrollViewRef.current?.scrollTo({ y: 200, animated: true })
    dismissScrollHint()
  }, [dismissScrollHint])

  const handleScroll = useCallback(
    (e: NativeSyntheticEvent<NativeScrollEvent>) => {
      if (e.nativeEvent.contentOffset.y > 20 && showScrollHint) {
        dismissScrollHint()
      }
    },
    [showScrollHint, dismissScrollHint],
  )

  // Reset to defaults when modal opens, using preselected nachkontrolle if specified
  useEffect(() => {
    if (visible) {
      // Don't set currentStep here - wait for companies to load
      setSelectedDate("") // Don't pre-select date
      setSelectedTime("")
      setInspectionType(bookingNachkontrollePreselected ? "nachkontrolle" : DEFAULT_INSPECTION_TYPE)
      setWalkIn(false)
      // FleetHub defaults every booking to Hol- und Bring-Service (ADR-0051 §5).
      // `walkIn` was already reset here but `serviceType` was not, so a reopen
      // after a Selbstvorfahren booking left the pair contradicting itself —
      // driveby on screen, `walkIn: false` on the wire. The hub gate below flips
      // it back to driveby when this location does not offer HBS.
      setServiceType("pickup_delivery")
      setAvailableCompanies([])
      setSelectedCompanyId(null)
      setCompanySearchQuery("")
      setLoadingCompanies(true)
      timesCacheRef.current = {} // Clear preload cache on modal open
      preloadingRef.current.clear()
      // PLA-151: reset Nachricht state on reopen (defense-in-depth — covers
      // parent-driven closes that bypass handleClose)
      setIncludeNachricht(false)
      setNachrichtText("")
      // Reset document state on reopen (mirrors Nachricht approach)
      setIncludeDocuments(false)
      setDocumentFiles([])
      setDocumentValidationError(null)
      setAttachSucceeded(null)
      setAttachError(null)
      setIsAttaching(false)
    }
  }, [visible, bookingNachkontrollePreselected])

  // Set initial step after companies load
  useEffect(() => {
    if (visible && !loadingCompanies && availableCompanies.length > 0) {
      setCurrentStep(1)
    }
  }, [visible, loadingCompanies, availableCompanies.length])

  // Close the inline service dropdown on outside click (web only — Fleet Hub
  // runs on react-native-web and the ref holds a DOM node there).
  useEffect(() => {
    if (!serviceDropdownOpen || Platform.OS !== "web") return
    const handler = (e: MouseEvent) => {
      const el = serviceDropdownRef.current as unknown as HTMLElement | null
      if (el && !el.contains(e.target as Node)) setServiceDropdownOpen(false)
    }
    document.addEventListener("mousedown", handler)
    return () => document.removeEventListener("mousedown", handler)
  }, [serviceDropdownOpen])

  // Fetch available companies when modal opens
  useEffect(() => {
    const fetchCompanies = async () => {
      if (!visible) return

      setLoadingCompanies(true)
      try {
        console.log("[BookInspectionModal] Fetching customer companies...")
        const companiesData = await getCustomerCompanies()

        // Build array of all companies (parent + children)
        const allCompanies = [companiesData.parentCompany, ...companiesData.childCompanies]

        setAvailableCompanies(allCompanies)

        // Auto-select parent if it's the only company
        if (allCompanies.length === 1) {
          setSelectedCompanyId(companiesData.parentCompany.id)
          console.log(
            "[BookInspectionModal] Auto-selected parent company:",
            companiesData.parentCompany.name,
          )
        }

        console.log("[BookInspectionModal] Companies loaded:", allCompanies.length, "total")
      } catch (error) {
        console.error("[BookInspectionModal] Error fetching companies:", error)
        // getCustomerCompanies() already captures genuine errors to Sentry
        // internally (ADR-0020). Expected validation codes (not-found,
        // failed-precondition, unauthenticated) are suppressed there. No
        // double-capture here.
        // On error, keep availableCompanies empty (already reset to [])
      } finally {
        setLoadingCompanies(false)
      }
    }

    fetchCompanies()
  }, [visible])

  // Preload optimal times for the next 2 available dates in background (fire-and-forget)
  const preloadAdjacentDates = useCallback(
    (
      afterDate: string,
      locationToUse: string,
      svcType: "pickup_delivery" | "driveby",
      legMinutes: number | undefined,
      hours: OpeningHoursResponse,
    ) => {
      let preloaded = 0
      for (let i = 1; i <= 7 && preloaded < 2; i++) {
        const candidate = dayjs(afterDate).add(i, "day")
        const candidateStr = candidate.format("YYYY-MM-DD")
        if (isDateDisabled(candidate, hours)) continue
        const preloadCacheKey = `${candidateStr}|${locationToUse}|${svcType}`
        if (timesCacheRef.current[preloadCacheKey] || preloadingRef.current.has(preloadCacheKey)) {
          preloaded++
          continue
        }

        preloadingRef.current.add(preloadCacheKey)
        preloaded++

        // Determine currentTime for this candidate date
        const isCandidateToday = candidate.isSame(dayjs(), "day")
        const dayNames = [
          "sunday",
          "monday",
          "tuesday",
          "wednesday",
          "thursday",
          "friday",
          "saturday",
        ]
        const dayName = dayNames[candidate.day()] as keyof WeeklySchedule
        const daySchedule = hours.schedule?.[dayName]
        const candidateCurrentTime = isCandidateToday
          ? dayjs().format("HH:mm")
          : daySchedule?.open || "08:00"

        // Fire and forget — no state updates, no loading spinners
        getOptimalBookingTimesActive(
          locationToUse,
          candidateStr,
          candidateCurrentTime,
          svcType,
          svcType === "pickup_delivery" ? legMinutes : undefined,
        )
          .then((result) => {
            preloadingRef.current.delete(preloadCacheKey)
            if (result) timesCacheRef.current[preloadCacheKey] = result
          })
          .catch(() => {
            preloadingRef.current.delete(preloadCacheKey)
          })
      }
    },
    [],
  )

  // Fetch opening hours and optimal times when modal opens
  // Combined into single effect to avoid race condition with expansionRegion state
  useEffect(() => {
    const fetchData = async () => {
      if (!visible) {
        console.log("[BookInspectionModal] Modal not visible, skipping data fetch")
        return
      }

      // Claim this run. A later run (e.g. the user switching company again
      // before this one's requests settle) bumps the ref, and this run's
      // isStale() checks below make it skip applying its now-superseded
      // opening-hours / HBS result. See Codex P2 on PR #192.
      const requestId = ++fetchRequestIdRef.current
      const isStale = () => fetchRequestIdRef.current !== requestId

      console.log("[BookInspectionModal] 🚀 Starting data fetch (opening hours + optimal times)...")
      // Only show full loading spinner on initial load, not when just switching dates
      if (!openingHours) {
        setLoadingHours(true)
      }

      try {
        // Step 1: Get expansion region (from selected company or customer profile)
        console.log("[BookInspectionModal] Step 1: Determining expansion region...")
        let locationToUse = "Nürnberg" // Default fallback

        let estimatedLegMinutes: number | undefined
        if (selectedCompanyId && availableCompanies.length > 0) {
          // Use selected company's expansion region and driving time
          const selectedCompany = availableCompanies.find((c) => c.id === selectedCompanyId)
          estimatedLegMinutes = selectedCompany?.estimatedLegMinutes
          if (selectedCompany?.expansionRegion) {
            locationToUse = selectedCompany.expansionRegion
            console.log(
              "[BookInspectionModal] Using selected company expansion region:",
              locationToUse,
              "for company:",
              selectedCompany.name,
            )
          } else {
            console.warn(
              "[BookInspectionModal] Selected company has no expansion region, fetching customer default...",
            )
            const region = await getCustomerExpansionRegion()
            locationToUse = region || "Nürnberg"
          }
        } else {
          // Fallback to customer's expansion region
          console.log(
            "[BookInspectionModal] No company selected, fetching customer expansion region...",
          )
          const region = await getCustomerExpansionRegion()
          locationToUse = region || "Nürnberg"
        }

        console.log("[BookInspectionModal] Location to use:", locationToUse)

        // Store expansion region immediately
        setExpansionRegion(locationToUse)

        // Step 2: Fetch opening hours (includes resource config)
        console.log(
          "[BookInspectionModal] Step 2: Fetching opening hours for location:",
          locationToUse,
        )
        const hours = await getLocationOpeningHours(locationToUse)

        if (isStale()) {
          // A newer company/date selection started its own fetch while this
          // one was in flight — never let this response's HBS gate or
          // opening hours land on top of whatever the current selection has.
          console.log(
            "[BookInspectionModal] Discarding stale opening-hours response for",
            locationToUse,
          )
          return
        }

        console.log("[BookInspectionModal] ✅ Opening hours received:", {
          hasSchedule: !!hours.schedule,
          timezone: hours.timezone,
          hasSpecialDates: !!hours.specialDates,
          hasResourceConfig: !!hours.resourceConfig,
          resourceConfig: hours.resourceConfig,
          mondayData: hours.schedule?.monday,
        })

        setOpeningHours(hours)

        // Step 2b: Hubs that don't offer Hol- und Bring-Service force
        // Selbstvorfahren (ADR-0051). Done here rather than in a follow-up
        // effect so the very first slot search already asks for the right
        // service type — the backend would answer an HBS search at such a hub
        // with `pickupNotAvailable`, and we'd fetch twice. The reverse flip is
        // deliberately absent: moving to a hub that *does* offer HBS re-enables
        // the button but never drags the customer's choice back to it.
        const hbsDisabled = isHbsDisabledAtHub(hours)
        if (hbsDisabled) {
          if (serviceType !== "driveby") {
            console.log("[BookInspectionModal] HBS disabled at", locationToUse, "→ Selbstvorfahren")
            setOptimalTimes(null)
            setSelectedTime("")
          }
          // Set both unconditionally: they converge, so React bails out when
          // nothing changed, and `walkIn` cannot be left out of step with
          // `serviceType` on a reopen.
          setServiceType("driveby")
          setWalkIn(true)
        }

        // Step 3: Fetch optimal times immediately (if date is selected)
        if (selectedDate) {
          // Auto-switch to driveby on Saturdays
          const selectedDateObj = dayjs(selectedDate)
          const isSaturday = selectedDateObj.day() === 6
          if (isSaturday && serviceType !== "driveby") {
            setServiceType("driveby")
            setWalkIn(true)
            setOptimalTimes(null)
            setSelectedTime("")
          }

          // Use local effective values — state updates are async so serviceType
          // may still be stale in this render when Saturday or a hub that does
          // not offer HBS forces driveby.
          const effectiveServiceType = isSaturday || hbsDisabled ? "driveby" : serviceType
          const effectiveLegMinutes =
            effectiveServiceType === "pickup_delivery" ? estimatedLegMinutes : undefined

          // Check preload cache first — instant display, no spinner
          const cacheKey = `${selectedDate}|${locationToUse}|${effectiveServiceType}`
          const cached = timesCacheRef.current[cacheKey]
          if (cached) {
            console.log("[BookInspectionModal] ⚡ Cache hit for", selectedDate)
            setOptimalTimes(cached)
            setLoadingOptimalTimes(false)
            setFetchTimeout(false)
            preloadAdjacentDates(
              selectedDate,
              locationToUse,
              effectiveServiceType,
              effectiveLegMinutes,
              hours,
            )
          } else {
            console.log("[BookInspectionModal] Step 3: Fetching optimal times...")
            setLoadingOptimalTimes(true)
            setFetchTimeout(false) // Reset timeout flag

            // Set 20-second timeout
            const timeoutId = setTimeout(() => {
              if (isStale()) {
                // A newer selection took over while this run was in flight —
                // its watchdog must not raise `fetchTimeout` over the slots the
                // current selection is showing (or waiting for).
                return
              }
              console.warn("[BookInspectionModal] ⏱️ Optimization timeout after 20s")
              setFetchTimeout(true)
              setLoadingOptimalTimes(false)
            }, 20000)
            optimalTimesTimeoutRef.current = timeoutId

            // Only ever cancels this run's own watchdog: by the time a stale run
            // settles, the ref may already hold the newer run's timer.
            const clearOwnTimeout = () => {
              clearTimeout(timeoutId)
              if (optimalTimesTimeoutRef.current === timeoutId) {
                optimalTimesTimeoutRef.current = null
              }
            }

            try {
              const isToday = selectedDateObj.isSame(dayjs(), "day")

              let currentTime: string
              if (isToday) {
                currentTime = dayjs().format("HH:mm")
              } else {
                const dayNames = [
                  "sunday",
                  "monday",
                  "tuesday",
                  "wednesday",
                  "thursday",
                  "friday",
                  "saturday",
                ]
                const dayName = dayNames[selectedDateObj.day()] as keyof WeeklySchedule
                const daySchedule = hours.schedule?.[dayName]
                currentTime = daySchedule?.open || "08:00"
              }

              console.log("[BookInspectionModal] Fetching optimal times with params:", {
                location: locationToUse,
                date: selectedDate,
                currentTime,
                isToday,
                serviceType: effectiveServiceType,
              })

              const results = await getOptimalBookingTimesActive(
                locationToUse,
                selectedDate,
                currentTime,
                effectiveServiceType,
                effectiveLegMinutes,
              )

              clearOwnTimeout()

              // Still cache it — it's valid for its own cacheKey — but don't
              // let a superseded selection's result overwrite the screen.
              timesCacheRef.current[cacheKey] = results
              if (isStale()) {
                console.log("[BookInspectionModal] Discarding stale optimal-times response")
                return
              }

              console.log("[BookInspectionModal] ✅ Optimal times received:", {
                recommendedTime: results.recommendedTime,
                goodTimesCount: results.goodTimes.length,
                busyTimesCount: results.busyTimes.length,
                allSlotsCount: results.allSlots.length,
                engineerUtilization: results.capacity.engineerUtilization,
              })

              setOptimalTimes(results)

              // Preload next 2 available dates in background
              preloadAdjacentDates(
                selectedDate,
                locationToUse,
                effectiveServiceType,
                effectiveLegMinutes,
                hours,
              )
            } catch (optimalError) {
              clearOwnTimeout()
              if (isStale()) {
                // Superseded run: the user is looking at another company/date,
                // so this failure blocks nobody (ADR-0036 → warn, not capture)
                // and must not blank the slots the current selection loaded.
                console.warn(
                  "[BookInspectionModal] Discarding stale optimal-times failure:",
                  optimalError,
                )
              } else {
                console.error(
                  "[BookInspectionModal] ❌ Failed to fetch optimal times:",
                  optimalError,
                )
                Sentry.captureException(optimalError, {
                  tags: { flow: "booking", operation: "fetch-optimal-times" },
                })
                setOptimalTimes(null)
                setFetchTimeout(true)
              }
            } finally {
              if (!isStale()) {
                setLoadingOptimalTimes(false)
              }
            }
          }
        }
      } catch (error) {
        if (isStale()) {
          // Same reasoning as the optimal-times catch: a superseded run's
          // failure blocks nobody, and its Nürnberg fallback below would drag
          // the current selection back to the wrong hub (and its HBS gate).
          console.warn("[BookInspectionModal] Discarding stale opening-hours failure:", error)
          return
        }
        console.error("[BookInspectionModal] ❌ Failed to fetch opening hours:", error)
        Sentry.captureException(error, {
          tags: { flow: "booking", operation: "fetch-opening-hours" },
        })
        // Even on error, try to load default Nürnberg hours
        try {
          console.log("[BookInspectionModal] Attempting fallback to Nürnberg hours...")
          const hours = await getLocationOpeningHours("Nürnberg")

          if (isStale()) {
            console.log("[BookInspectionModal] Discarding stale fallback opening-hours response")
            return
          }

          console.log("[BookInspectionModal] Fallback hours received:", hours)
          setOpeningHours(hours)
          setExpansionRegion("Nürnberg")
          // Same HBS gate as the happy path — the fallback hub is a real hub.
          if (isHbsDisabledAtHub(hours)) {
            if (serviceType !== "driveby") {
              setOptimalTimes(null)
              setSelectedTime("")
            }
            setServiceType("driveby")
            setWalkIn(true)
          }
        } catch (fallbackError) {
          console.error(
            "[BookInspectionModal] ❌ Failed to fetch fallback opening hours:",
            fallbackError,
          )
          Sentry.captureException(fallbackError, {
            tags: { flow: "booking", operation: "fetch-opening-hours" },
            extra: { context: "fallback-nuernberg" },
          })
        }
      } finally {
        if (!isStale()) {
          // Leave the spinners to the run that now owns the screen. Reaching
          // here un-superseded means this run is fully settled, so nothing is
          // loading any more — including when it bailed out at opening hours
          // while an older run's (now ignored) times request is still pending.
          setLoadingHours(false)
          setLoadingOptimalTimes(false)
        }
        console.log("[BookInspectionModal] Data fetch complete")
      }
    }

    fetchData()

    return () => {
      // Runs before the superseding fetch (and on close/unmount): drop the
      // outgoing run's watchdog so it can never fire against the next one.
      if (optimalTimesTimeoutRef.current !== null) {
        clearTimeout(optimalTimesTimeoutRef.current)
        optimalTimesTimeoutRef.current = null
      }
    }
  }, [visible, selectedDate, nachkontrolle, selectedCompanyId, availableCompanies, serviceType])

  // Auto-select first available date when entering dateTime step
  useEffect(() => {
    if (currentStep === stepMapping.dateTime && !selectedDate && openingHours) {
      for (let i = 0; i < 14; i++) {
        const candidate = dayjs().add(i, "day")
        if (!isDateDisabled(candidate, openingHours)) {
          setSelectedDate(candidate.format("YYYY-MM-DD"))
          break
        }
      }
    }
  }, [currentStep, stepMapping.dateTime, selectedDate, openingHours])

  // Monitor opening hours state changes for debugging
  useEffect(() => {
    console.log("[BookInspectionModal] 📊 State update - openingHours:", {
      isNull: openingHours === null,
      hasSchedule: !!openingHours?.schedule,
      timezone: openingHours?.timezone,
      mondaySchedule: openingHours?.schedule?.monday,
    })
  }, [openingHours])

  // Monitor loading state changes
  useEffect(() => {
    console.log("[BookInspectionModal] 📊 State update - loadingHours:", loadingHours)
  }, [loadingHours])

  /**
   * Handle form submission
   */
  const handleSubmit = async () => {
    // Validate inputs
    if (!selectedDate || !selectedTime) {
      return
    }

    // Debug logging to see what's being sent
    console.log("[BookInspectionModal] 🔍 COMPANY SELECTION DEBUG:")
    console.log("[BookInspectionModal] - selectedCompanyId:", selectedCompanyId)
    console.log("[BookInspectionModal] - hasMultipleCompanies:", hasMultipleCompanies)
    console.log("[BookInspectionModal] - availableCompanies:", availableCompanies)

    const selectedCompany = availableCompanies.find((c) => c.id === selectedCompanyId)
    console.log("[BookInspectionModal] - selectedCompany:", selectedCompany)

    // Determine if this is a parent or child company
    // Parent company is always the first item in availableCompanies array
    const parentCompanyId = availableCompanies[0]?.id
    const isParentCompany = selectedCompanyId === parentCompanyId

    // Only send childCompanyId if it's actually a child company
    // For parent company bookings, send undefined so backend uses parent company data
    const childCompanyIdToSend = isParentCompany ? undefined : selectedCompanyId || undefined

    // Format number plate if provided and valid
    let formattedNumberPlate: string | undefined
    if (includeNumberPlate && numberPlate.trim()) {
      // Validate the number plate format
      if (validatePlate(numberPlate)) {
        const [city, letters, numbers] = parsePlateIntoParts(numberPlate)
        formattedNumberPlate = formatPartsForHubSpot(city, letters, numbers)
      }
      // If invalid, silently omit from order (don't show error to user)
    }

    // Ghost order card draft (PLA-433): the known booking fields, held in the
    // store so the ghost card renders a real row layout while the HubSpot→
    // Firestore sync runs. Prüfstelle/order#/progress are server-assigned and
    // render as shimmer. Set immediately before the booking call in both paths.
    const pendingDraft = {
      abholdatum: selectedDate,
      spatesteAbholung: selectedTime,
      serviceType,
      numberPlate: formattedNumberPlate,
      companyName: selectedCompany?.name,
      nachkontrolle,
    }

    console.log("[BookInspectionModal] 🚀 Creating order with:", {
      abholdatum: selectedDate,
      spatesteAbholung: selectedTime,
      nachkontrolle,
      walkIn,
      childCompanyId: childCompanyIdToSend,
      numberPlate: formattedNumberPlate,
      isParentCompany,
      parentCompanyId,
      selectedDateType: typeof selectedDate,
      selectedTimeType: typeof selectedTime,
    })

    // ── Two-phase submit when documents are selected (PLA-122 / ADR-0022) ────
    // When the user has toggled "Dokumente hinzufügen" and selected files, we
    // call createFleetHubOrder directly to capture the ticketId before the
    // store action closes the modal, then immediately attach the documents.
    // On attach failure the modal stays open and shows a retry banner.
    if (includeDocuments && documentFiles.length > 0) {
      setIsAttaching(true)
      setAttachError(null)
      setAttachSucceeded(null)
      setLocalBookingError(null)

      try {
        const trimmedNotes = nachrichtText.trim()
        const orderResult = await createFleetHubOrder({
          abholdatum: selectedDate,
          spatesteAbholung: selectedTime,
          nachkontrolle,
          walkIn,
          childCompanyId: childCompanyIdToSend,
          numberPlate: formattedNumberPlate,
          inspectionType,
          ...(includeNachricht && trimmedNotes ? { customerNotes: trimmedNotes } : {}),
        })

        const { ticketId } = orderResult
        pendingTicketIdRef.current = ticketId

        // Publish the ghost-card draft before the loading card is shown. (PLA-433)
        setPendingOrderDraft(pendingDraft)

        // Start Pusher + polling immediately so the order appears as soon as it syncs,
        // regardless of whether the attach step succeeds.
        startPostBookingFlow(ticketId).catch(() => {
          // best-effort — polling fallback still covers this
        })

        // Step 2: attach documents
        await _attachDocuments(ticketId, documentFiles)
        setAttachSucceeded(true)
        setDocumentFiles([])
        // Codex P1 on PR #132: BookInspectionModal stays mounted across opens
        // (only `visible` toggles). Without resetting isAttaching here, the
        // next open would show the spinner + disabled CTA on a fresh modal.
        setIsAttaching(false)
        onClose()
      } catch (error) {
        setIsAttaching(false)
        if (error instanceof BookingDocumentError) {
          // Booking succeeded, attach failed — show inline retry banner
          setAttachError(error.userMessage)
          setAttachSucceeded(false)
          return
        }
        // Codex P2 on PR #132: booking creation itself failed in the docs
        // path. The standard (no-docs) path gets a user-facing message via
        // the store's bookingError; we have to surface our own here or the
        // failure is silent for users.
        const message =
          error instanceof Error
            ? error.message
            : "Buchung konnte nicht erstellt werden. Bitte erneut versuchen."
        setLocalBookingError(message)
        console.error("[BookInspectionModal] Error submitting order (with docs):", error)
        Sentry.captureException(error, {
          tags: { flow: "booking", operation: "submit-order-with-docs" },
        })
      }
      return
    }

    // ── Standard submit path (no documents) ──────────────────────────────────
    const trimmedNotes = nachrichtText.trim()
    // Publish the ghost-card draft before createFleetOrder shows the loading card. (PLA-433)
    setPendingOrderDraft(pendingDraft)
    try {
      await createFleetOrder({
        abholdatum: selectedDate,
        spatesteAbholung: selectedTime,
        nachkontrolle,
        walkIn,
        childCompanyId: childCompanyIdToSend,
        numberPlate: formattedNumberPlate,
        inspectionType,
        ...(includeNachricht && trimmedNotes ? { customerNotes: trimmedNotes } : {}),
      })
    } catch (error) {
      // Error is handled in store
      console.error("[BookInspectionModal] Error submitting order:", error)
      Sentry.captureException(error, {
        tags: { flow: "booking", operation: "submit-order" },
      })
    }
  }

  /** Inner helper: upload files to the backend, throws BookingDocumentError on failure. */
  const _attachDocuments = async (ticketId: string, files: File[]): Promise<void> => {
    await uploadBookingDocumentsB2b(ticketId, files)
  }

  /**
   * Retry attaching documents after a previous failed attempt (ADR-0022).
   * The in-memory file bundle is re-used; ticketId comes from the ref set during submit.
   */
  const handleRetryAttach = async () => {
    const ticketId = pendingTicketIdRef.current
    if (!ticketId || documentFiles.length === 0) return

    setIsAttaching(true)
    setAttachError(null)

    try {
      await _attachDocuments(ticketId, documentFiles)
      setAttachSucceeded(true)
      setIsAttaching(false)
      setDocumentFiles([])
      onClose()
    } catch (error) {
      setIsAttaching(false)
      const docError =
        error instanceof BookingDocumentError
          ? error
          : new BookingDocumentError("internal", "Dokumente konnten nicht hochgeladen werden.")
      setAttachError(docError.userMessage)
    }
  }

  /**
   * Handle booking (alias for handleSubmit)
   */
  const handleBooking = handleSubmit

  /**
   * Handle modal close.
   * If the user closes after a booking succeeded but documents were not attached,
   * fire a Sentry warning so ops can manually attach files if needed (ADR-0022).
   */
  const handleClose = () => {
    if (attachSucceeded === false && pendingTicketIdRef.current && documentFiles.length > 0) {
      Sentry.captureMessage("Booking documents abandoned after failed attach", {
        level: "warning",
        tags: {
          flow: "booking",
          operation: "attach-documents-abandoned",
          orderId: pendingTicketIdRef.current,
          surface: "fleethub",
        },
      })
    }
    // Clear document + Nachricht state on close
    setDocumentFiles([])
    setAttachError(null)
    setAttachSucceeded(null)
    setLocalBookingError(null)
    pendingTicketIdRef.current = null
    // PLA-151: reset Nachricht transient state so reopen starts fresh
    setIncludeNachricht(false)
    setNachrichtText("")
    onClose()
  }

  // Defense-in-depth reset on modal reopen. The modal stays mounted across
  // sessions (only `visible` toggles), so any state set during a previous
  // session can leak into the next open if the user closed via the OS back
  // button or a parent-driven close that bypassed `handleClose`. See Codex
  // P2 on PR #132 — localBookingError was the specific leak that prompted
  // this, but clearing the full document-related state on reopen is the
  // safest stance.
  useEffect(() => {
    if (visible) {
      setLocalBookingError(null)
      setAttachError(null)
      setAttachSucceeded(null)
      setDocumentValidationError(null)
      setIsAttaching(false)
    }
  }, [visible])

  /**
   * Handle back navigation
   */
  const handleBack = () => {
    if (currentStep === stepMapping.dateTime && hasMultipleCompanies) {
      // Going back from dateTime to company selection
      setCurrentStep(stepMapping.company!)
    } else if (currentStep === stepMapping.summary) {
      // Going back from summary to dateTime
      setCurrentStep(stepMapping.dateTime)
    }
  }

  // ── Document file management helpers (PLA-122) ───────────────────────────

  const ALLOWED_MIME_TYPES = new Set(["application/pdf", "image/jpeg", "image/png", "image/heic"])
  const MAX_FILE_SIZE_BYTES = 5 * 1024 * 1024 // 5 MB
  const MAX_FILES = 3

  const handleDocumentFilesSelected = (newFiles: FileList | File[]) => {
    setDocumentValidationError(null)
    const incoming = Array.from(newFiles)
    const combined = [...documentFiles, ...incoming]

    if (combined.length > MAX_FILES) {
      setDocumentValidationError(`Maximal ${MAX_FILES} Dokumente erlaubt.`)
      return
    }

    for (const file of incoming) {
      if (!ALLOWED_MIME_TYPES.has(file.type)) {
        setDocumentValidationError(
          `Nicht erlaubter Dateityp: "${file.name}". Erlaubt: PDF, JPEG, PNG, HEIC.`,
        )
        return
      }
      if (file.size > MAX_FILE_SIZE_BYTES) {
        const sizeMB = (file.size / (1024 * 1024)).toFixed(1)
        setDocumentValidationError(
          `"${file.name}" ist zu groß (${sizeMB} MB). Maximal 5 MB pro Datei.`,
        )
        return
      }
    }

    setDocumentFiles(combined)
  }

  const handleRemoveDocumentFile = (index: number) => {
    setDocumentFiles((prev) => prev.filter((_, i) => i !== index))
    setDocumentValidationError(null)
  }

  const formatFileSize = (bytes: number): string => {
    if (bytes >= 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
    return `${Math.round(bytes / 1024)} KB`
  }

  // ── End document file management ──────────────────────────────────────────

  /**
   * Handle phone call
   */
  const handlePhoneCall = () => {
    if (Platform.OS === "web" && typeof window !== "undefined") {
      window.location.href = "tel:+499118808306"
    } else {
      Linking.openURL("tel:+499118808306")
    }
  }

  /**
   * Handle WhatsApp contact
   */
  const handleWhatsApp = () => {
    const phoneNumber = "499118808306" // German format without leading 0
    const whatsappUrl = `https://wa.me/${phoneNumber}`

    if (Platform.OS === "web" && typeof window !== "undefined") {
      window.open(whatsappUrl, "_blank")
    } else {
      Linking.openURL(whatsappUrl)
    }
  }

  return (
    <Modal visible={visible} transparent animationType="fade" onRequestClose={handleClose}>
      <View style={$modalOverlay}>
        <TouchableOpacity style={$overlayTouchable} activeOpacity={1} onPress={handleClose} />

        <View style={$modalContainer}>
          {/* Header */}
          <View style={$header}>
            <Text style={$headerTitle}>
              {currentStep === stepMapping.dateTime
                ? "Datum und Zeit"
                : currentStep === stepMapping.summary
                  ? "Buchung bestätigen"
                  : "Neue Inspektion buchen"}
            </Text>
            <TouchableOpacity onPress={handleClose} style={$closeButton}>
              <Icon name="close" size={24} color={colors.text} />
            </TouchableOpacity>
          </View>

          {/* Service Type Toggle */}
          {(() => {
            const selectedDateObj = selectedDate ? dayjs(selectedDate) : null
            const isSaturdayToggle = selectedDateObj?.day() === 6
            // Either condition greys the HBS button; the location hint wins when
            // both apply, because "this hub never offers it" is the more useful
            // thing to tell a customer than "not on Saturdays" (ADR-0051 §5).
            const hbsBlocked = isSaturdayToggle || hbsDisabledAtHub
            return (
              <View style={$serviceToggleContainer}>
                <View style={$serviceToggle}>
                  <TouchableOpacity
                    style={[
                      $serviceToggleButton,
                      serviceType === "pickup_delivery" &&
                        !hbsBlocked &&
                        $serviceToggleButtonActive,
                      hbsBlocked && { opacity: 0.5 },
                    ]}
                    accessibilityRole="button"
                    accessibilityState={{ disabled: hbsBlocked }}
                    onPress={() => {
                      if (!hbsBlocked) {
                        setServiceType("pickup_delivery")
                        setWalkIn(false)
                        setOptimalTimes(null)
                        setSelectedTime("")
                        if (currentStep !== stepMapping.company) {
                          setCurrentStep(stepMapping.dateTime)
                        }
                        timesCacheRef.current = {}
                        preloadingRef.current.clear()
                        if (selectedDate) setLoadingOptimalTimes(true)
                      }
                    }}
                    activeOpacity={hbsBlocked ? 1 : 0.7}
                  >
                    <Text
                      style={[
                        $serviceToggleText,
                        serviceType === "pickup_delivery" &&
                          !hbsBlocked &&
                          $serviceToggleTextActive,
                      ]}
                    >
                      Hol- und Bring-Service
                    </Text>
                  </TouchableOpacity>
                  <TouchableOpacity
                    style={[
                      $serviceToggleButton,
                      serviceType === "driveby" && $serviceToggleButtonActive,
                    ]}
                    onPress={() => {
                      setServiceType("driveby")
                      setWalkIn(true)
                      setOptimalTimes(null)
                      setSelectedTime("")
                      if (currentStep !== stepMapping.company) {
                        setCurrentStep(stepMapping.dateTime)
                      }
                      timesCacheRef.current = {}
                      preloadingRef.current.clear()
                      if (selectedDate) setLoadingOptimalTimes(true)
                    }}
                    activeOpacity={0.7}
                  >
                    <Text
                      style={[
                        $serviceToggleText,
                        serviceType === "driveby" && $serviceToggleTextActive,
                      ]}
                    >
                      Selbstvorfahren
                    </Text>
                  </TouchableOpacity>
                </View>
                {hbsBlocked && (
                  <View style={$saturdayHint}>
                    <Icon name="info-outline" size={12} color={colors.modalSlateText} />
                    <Text style={$saturdayHintText}>
                      {hbsDisabledAtHub
                        ? "Hol- und Bring-Service ist an diesem Standort aktuell nicht verfügbar"
                        : "Samstags nur Selbstvorfahren verfügbar"}
                    </Text>
                  </View>
                )}
              </View>
            )
          })()}

          {/* Progress Bar */}
          <WizardProgressBar
            currentStep={currentStep}
            totalSteps={stepMapping.total}
            onBack={handleBack}
            showBackButton={currentStep > 1}
          />

          <ScrollView
            ref={scrollViewRef}
            contentContainerStyle={$content}
            style={$scrollViewFlex}
            showsVerticalScrollIndicator={false}
            onScroll={handleScroll}
            scrollEventThrottle={100}
          >
            {/* Loading indicator while fetching companies */}
            {loadingCompanies && (
              <View style={$loadingContainer}>
                <ActivityIndicator size="large" color={colors.primary} />
                <Text style={$loadingText}>Lade Firmenstandorte...</Text>
              </View>
            )}

            {/* Step 1: Company Selection (only if multiple companies) */}
            {!loadingCompanies && currentStep === stepMapping.company && hasMultipleCompanies && (
              <View style={$stepContainer}>
                <Text style={$stepTitle}>Firmenstandort auswählen</Text>

                {/* Search Input */}
                <TextField
                  value={companySearchQuery}
                  onChangeText={setCompanySearchQuery}
                  placeholder="Firmenstandort suchen..."
                  autoCapitalize="none"
                  autoCorrect={false}
                  containerStyle={$searchFieldContainer}
                  inputWrapperStyle={$searchInputContainer}
                  style={$searchInputText}
                  LeftAccessory={() => (
                    <View style={$searchIconAccessory}>
                      <Icon name="search" size={20} color={colors.textDim} />
                    </View>
                  )}
                  RightAccessory={
                    companySearchQuery.length > 0
                      ? () => (
                          <TouchableOpacity
                            style={$clearButton}
                            onPress={() => setCompanySearchQuery("")}
                            accessibilityLabel="Suche löschen"
                          >
                            <Icon name="close" size={16} color={colors.text} />
                          </TouchableOpacity>
                        )
                      : undefined
                  }
                />

                {/* Scrollable Company List */}
                <ScrollView
                  style={$companyScrollView}
                  showsVerticalScrollIndicator={true}
                  nestedScrollEnabled={true}
                >
                  {filteredCompanies.length > 0 ? (
                    filteredCompanies.map((company) => (
                      <TouchableOpacity
                        key={company.id}
                        style={$companyOption}
                        onPress={() => {
                          const isSameCompany = selectedCompanyId === company.id
                          setSelectedCompanyId(company.id)
                          setSelectedTime("")
                          setOptimalTimes(null)
                          // If re-selecting the same company, clear and re-set date to force refetch
                          if (isSameCompany && selectedDate) {
                            const prevDate = selectedDate
                            setSelectedDate("")
                            setTimeout(() => setSelectedDate(prevDate), 0)
                          }
                          // Auto-advance to date+time selection step
                          setCurrentStep(stepMapping.dateTime)
                        }}
                        accessibilityRole="radio"
                        accessibilityState={{ checked: selectedCompanyId === company.id }}
                      >
                        <View style={$radioButton}>
                          {selectedCompanyId === company.id && (
                            <View style={$radioButtonSelected} />
                          )}
                        </View>
                        <View style={$companyInfo}>
                          <Text style={$companyName}>{company.name}</Text>
                          {company.expansionRegion && (
                            <Text style={$companyRegion}>{company.expansionRegion}</Text>
                          )}
                        </View>
                      </TouchableOpacity>
                    ))
                  ) : (
                    <View style={$noResultsContainer}>
                      <Icon name="search-off" size={48} color={colors.textDim} />
                      <Text style={$noResultsText}>Keine Ergebnisse gefunden</Text>
                    </View>
                  )}
                </ScrollView>
              </View>
            )}

            {/* Step: Combined Date + Time Selection */}
            {!loadingCompanies && currentStep === stepMapping.dateTime && (
              <View style={$stepContainer}>
                {loadingHours ? (
                  <View style={$loadingContainer}>
                    <ActivityIndicator size="large" color={colors.primary} />
                    <Text style={$loadingText}>Öffnungszeiten werden geladen...</Text>
                  </View>
                ) : (
                  <DateTimeSelector
                    selectedDate={selectedDate}
                    selectedTime={selectedTime}
                    onDateChange={(date) => {
                      setSelectedDate(date)
                      setSelectedTime("")
                    }}
                    onTimeSelect={(time) => {
                      setSelectedTime(time)
                      setCurrentStep(stepMapping.summary)
                    }}
                    openingHours={openingHours}
                    optimalTimes={optimalTimes}
                    loadingOptimalTimes={loadingOptimalTimes}
                    fetchTimeout={fetchTimeout}
                    serviceType={serviceType}
                    onSwitchToDriveby={
                      serviceType === "pickup_delivery"
                        ? () => {
                            setServiceType("driveby")
                            setWalkIn(true)
                            setOptimalTimes(null)
                            setSelectedTime("")
                            if (selectedDate) setLoadingOptimalTimes(true)
                          }
                        : undefined
                    }
                  />
                )}
              </View>
            )}

            {/* Summary Step — Redesigned to match OrbitControl */}
            {!loadingCompanies && currentStep === stepMapping.summary && (
              <View style={$stepContainer}>
                {/* Summary card — widget "DEIN TERMIN" pattern */}
                <View style={$summaryCardV2}>
                  <View style={$terminCardInner}>
                    {/* Header row */}
                    <View style={$terminHeaderRow}>
                      <Text style={$terminLabel}>DEIN TERMIN</Text>
                      <Pressable
                        onPress={() => setCurrentStep(stepMapping.dateTime)}
                        disabled={isBookingOrder}
                      >
                        <Text style={$terminEditLink}>Ändern</Text>
                      </Pressable>
                    </View>

                    {/* Primary line: service dropdown + delivery type */}
                    <View style={$terminPrimaryRow}>
                      <View ref={serviceDropdownRef} style={$serviceTriggerWrap}>
                        <Pressable
                          onPress={() => !isBookingOrder && setServiceDropdownOpen((o) => !o)}
                          style={$serviceTrigger}
                          disabled={isBookingOrder}
                        >
                          <Text style={$serviceTriggerText}>
                            {getInspectionService(inspectionType).shortName}
                          </Text>
                          <Icon
                            name={serviceDropdownOpen ? "keyboard-arrow-up" : "keyboard-arrow-down"}
                            size={14}
                            color={colors.palette.neutral450}
                          />
                        </Pressable>

                        {serviceDropdownOpen && (
                          <View style={$serviceDropdownPanel}>
                            {INSPECTION_SERVICES.filter((s) => s.id !== inspectionType).map((s) => (
                              <Pressable
                                key={s.id}
                                onPress={() => {
                                  setInspectionType(s.id)
                                  setServiceDropdownOpen(false)
                                }}
                                style={({ hovered, pressed }: any) => [
                                  $serviceDropdownItem,
                                  (hovered || pressed) && $serviceDropdownItemHover,
                                ]}
                              >
                                <Text style={$serviceDropdownItemText}>{s.shortName}</Text>
                              </Pressable>
                            ))}
                          </View>
                        )}
                      </View>
                      <Text style={$terminDotSeparator}>·</Text>
                      <Text style={$terminDeliveryText}>
                        {serviceType === "pickup_delivery"
                          ? "Hol- und Bring-Service"
                          : "Selbstvorfahren"}
                      </Text>
                    </View>

                    {/* Secondary line: date · time */}
                    <Text style={$terminSecondaryLine}>
                      {selectedDate ? dayjs(selectedDate).format("ddd, DD. MMMM YYYY") : "—"}
                      {" · "}
                      {selectedTime ? formatTime(selectedTime) : "—"}
                    </Text>

                    {/* Optional multi-company location line */}
                    {hasMultipleCompanies && selectedCompanyId && (
                      <Text style={$terminLocationLine} numberOfLines={1}>
                        {availableCompanies.find((c) => c.id === selectedCompanyId)?.name}
                      </Text>
                    )}
                  </View>
                </View>

                {/* Options with toggle switches */}
                <View style={{ gap: 10, marginTop: 20 }}>
                  {/* Number Plate */}
                  <Pressable
                    style={[$toggleOption, includeNumberPlate && $toggleOptionActive]}
                    onPress={() => setIncludeNumberPlate(!includeNumberPlate)}
                  >
                    <View style={[$toggleTrack, includeNumberPlate && $toggleTrackActive]}>
                      <View style={[$toggleKnob, includeNumberPlate && $toggleKnobActive]} />
                    </View>
                    <View style={$toggleLabelContainer}>
                      <Text style={$toggleLabel}>Kennzeichen hinzufügen</Text>
                      <Text style={$toggleSublabel}>Optional</Text>
                    </View>
                  </Pressable>

                  {includeNumberPlate && (
                    <View style={$numberPlateContainer}>
                      <NumberPlateInput
                        value={numberPlate}
                        onChangeValue={setNumberPlate}
                        sizeMode="normal"
                      />
                    </View>
                  )}

                  {/* Nachricht toggle (PLA-151) */}
                  <Pressable
                    style={[$toggleOption, includeNachricht && $toggleOptionActive]}
                    onPress={() => setIncludeNachricht(!includeNachricht)}
                  >
                    <View style={[$toggleTrack, includeNachricht && $toggleTrackActive]}>
                      <View style={[$toggleKnob, includeNachricht && $toggleKnobActive]} />
                    </View>
                    <View style={$toggleLabelContainer}>
                      <Text style={$toggleLabel}>Nachricht hinzufügen</Text>
                      <Text style={$toggleSublabel}>Optional</Text>
                    </View>
                  </Pressable>

                  {includeNachricht && (
                    <View style={$nachrichtSection}>
                      <TextInput
                        style={$nachrichtInput}
                        value={nachrichtText}
                        onChangeText={(t) =>
                          setNachrichtText(
                            t.length <= NACHRICHT_MAX_CHARS ? t : t.slice(0, NACHRICHT_MAX_CHARS),
                          )
                        }
                        placeholder="Ihre Nachricht an uns..."
                        placeholderTextColor="#9CA3AF"
                        multiline
                        numberOfLines={3}
                        maxLength={NACHRICHT_MAX_CHARS}
                        textAlignVertical="top"
                      />
                      <Text style={$nachrichtCounter}>
                        {nachrichtText.length} / {NACHRICHT_MAX_CHARS}
                      </Text>
                    </View>
                  )}

                  {/* Documents toggle (PLA-122) */}
                  <Pressable
                    style={[$toggleOption, includeDocuments && $toggleOptionActive]}
                    onPress={() => {
                      setIncludeDocuments(!includeDocuments)
                      if (includeDocuments) {
                        setDocumentFiles([])
                        setDocumentValidationError(null)
                      }
                    }}
                  >
                    <View style={[$toggleTrack, includeDocuments && $toggleTrackActive]}>
                      <View style={[$toggleKnob, includeDocuments && $toggleKnobActive]} />
                    </View>
                    <View style={$toggleLabelContainer}>
                      <Text style={$toggleLabel}>Dokumente hinzufügen</Text>
                      <Text style={$toggleSublabel}>Optional</Text>
                    </View>
                  </Pressable>

                  {includeDocuments && (
                    <View style={$documentSection}>
                      {/* Drop-zone styled to match OrbitControl's ConfirmStep visual.
                          Native HTML <input> sits invisibly on top so click-anywhere
                          opens the picker. Drag-and-drop is web-only; non-web platforms
                          short-circuit to null (CarAssist FleetHub is web-only in prod). */}
                      {documentFiles.length < MAX_FILES && Platform.OS === "web" && (
                        <View style={$documentDropZone}>
                          <Icon name="file-upload" size={22} color={colors.modalSlateText} />
                          <Text style={$documentDropZonePrimary}>
                            Datei auswählen oder hierher ziehen
                          </Text>
                          <Text style={$documentDropZoneHint}>
                            PDF, JPG, PNG, HEIC · max. 5 MB · max. 3 Dateien
                          </Text>
                          {/* @ts-ignore — native <input> sits on top of the styled View */}
                          <input
                            type="file"
                            multiple
                            accept=".pdf,.jpg,.jpeg,.png,.heic,application/pdf,image/jpeg,image/png,image/heic"
                            style={webFileInputStyle}
                            onChange={(e: any) => {
                              if (e.target.files) {
                                handleDocumentFilesSelected(e.target.files)
                                e.target.value = ""
                              }
                            }}
                          />
                        </View>
                      )}

                      {/* Selected file list */}
                      {documentFiles.map((file, index) => (
                        <View key={`${file.name}-${index}`} style={$documentFileRow}>
                          <Icon name="description" size={16} color={colors.text} />
                          <View style={$documentFileInfo}>
                            <Text style={$documentFileName} numberOfLines={1}>
                              {file.name}
                            </Text>
                            <Text style={$documentFileSize}>{formatFileSize(file.size)}</Text>
                          </View>
                          <Pressable
                            onPress={() => handleRemoveDocumentFile(index)}
                            hitSlop={8}
                            style={$documentFileRemoveButton}
                          >
                            <Icon name="close" size={16} color={colors.modalSlateText} />
                          </Pressable>
                        </View>
                      ))}

                      {/* Client-side validation error */}
                      {documentValidationError && (
                        <View style={$errorContainerV2}>
                          <Icon name="error-outline" size={16} color={colors.primary} />
                          <Text style={$errorTextV2}>{documentValidationError}</Text>
                        </View>
                      )}
                    </View>
                  )}
                </View>

                {/* Booking error — from store (no-docs path) or local (docs path) */}
                {(bookingError || localBookingError) && (
                  <View style={$errorContainerV2}>
                    <Icon name="error-outline" size={18} color={colors.primary} />
                    <Text style={$errorTextV2}>{bookingError || localBookingError}</Text>
                  </View>
                )}

                {/* Attach retry banner (PLA-122 / ADR-0022) — shown after booking succeeded
                    but document attach failed. Keeps modal open so the user can retry. */}
                {attachSucceeded === false && attachError && (
                  <View style={$retryBannerContainer}>
                    <Icon name="check-circle-outline" size={16} color="#22863a" />
                    <Text style={$retryBannerText}>
                      {"Buchung erstellt ✓ — Dokumente konnten nicht hochgeladen werden. "}
                    </Text>
                    <Pressable onPress={handleRetryAttach} disabled={isAttaching}>
                      <Text style={$retryBannerLink}>
                        {isAttaching ? "Wird hochgeladen…" : "Erneut versuchen"}
                      </Text>
                    </Pressable>
                  </View>
                )}

                {/* CTA Button */}
                <View style={{ marginTop: 20 }}>
                  <Pressable
                    onPress={handleBooking}
                    disabled={isBookingOrder || isAttaching || !selectedDate || !selectedTime}
                    style={({ pressed }) => [
                      $ctaButton,
                      (isBookingOrder || isAttaching) && $ctaButtonLoading,
                      pressed && !isBookingOrder && !isAttaching && $ctaButtonPressed,
                    ]}
                  >
                    {isBookingOrder || isAttaching ? (
                      <>
                        <ActivityIndicator size="small" color={colors.surface} />
                        <Text style={$ctaButtonText}>
                          {isAttaching ? "Dokumente werden hochgeladen…" : "Wird gebucht…"}
                        </Text>
                      </>
                    ) : (
                      <>
                        <Text style={$ctaButtonText}>Jetzt buchen</Text>
                        <Icon name="arrow-forward" size={18} color={colors.surface} />
                      </>
                    )}
                  </Pressable>
                </View>
              </View>
            )}
          </ScrollView>

          {/* Scroll hint arrow */}
          {showScrollHint && currentStep === stepMapping.dateTime && (
            <Pressable onPress={handleScrollHintPress} style={$scrollHintTouchable}>
              <Animated.View
                style={[
                  $scrollHintArrow,
                  {
                    opacity: scrollHintOpacity,
                    transform: [{ translateY: scrollHintBounce }],
                  },
                ]}
              >
                <Icon name="keyboard-arrow-down" size={28} color={colors.text} />
              </Animated.View>
            </Pressable>
          )}

          {/* Support Footer — subtle, single-line */}
          <View style={$supportFooterCompact}>
            <Text style={$supportFooterLabel}>Noch Fragen?</Text>
            <Pressable onPress={handlePhoneCall} style={$supportFooterLink}>
              <Icon name="phone" size={12} color={colors.text} />
              <Text style={$supportFooterLinkText}>0911 8808 306</Text>
            </Pressable>
            <Text style={$supportFooterDivider}>|</Text>
            <Pressable onPress={handleWhatsApp} style={$supportFooterLink}>
              <Icon name="chat" size={12} color={colors.text} />
              <Text style={$supportFooterLinkText}>WhatsApp</Text>
            </Pressable>
          </View>
        </View>
      </View>
    </Modal>
  )
}

// Scroll hint arrow
const $scrollHintTouchable: ViewStyle = {
  position: "absolute",
  right: 6,
  bottom: 80,
  zIndex: 10,
  cursor: "pointer" as any,
}

const $scrollHintArrow: ViewStyle = {
  width: 36,
  height: 36,
  borderRadius: 18,
  backgroundColor: colors.surface,
  alignItems: "center",
  justifyContent: "center",
  // @ts-ignore - Web-specific shadow
  boxShadow: "0 2px 8px rgba(17, 30, 44, 0.2)",
  elevation: 4,
}

// Styles
const $modalOverlay: ViewStyle = {
  flex: 1,
  backgroundColor: "rgba(17, 30, 44, 0.5)",
  justifyContent: "center",
  alignItems: "center",
  padding: spacing.sm,
}

const $overlayTouchable: ViewStyle = {
  position: "absolute",
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
}

const $modalContainer: ViewStyle = {
  width: "100%",
  maxWidth: 500,
  flexShrink: 1,
  maxHeight: "95%",
  backgroundColor: colors.surface,
  borderRadius: 16,
  // @ts-ignore - Web-specific shadow
  boxShadow: "0 4px 24px rgba(17, 30, 44, 0.2)",
  elevation: 8,
  overflow: "hidden",
}

const $header: ViewStyle = {
  flexDirection: "row",
  justifyContent: "space-between",
  alignItems: "center",
  paddingHorizontal: spacing.md,
  paddingVertical: spacing.sm,
  borderBottomWidth: 1,
  borderBottomColor: colors.palette.neutral200,
}

const $headerTitle: TextStyle = {
  fontSize: 18,
  fontWeight: "700",
  color: colors.text,
}

const $closeButton: ViewStyle = {
  padding: spacing.xs,
  borderRadius: 8,
  cursor: "pointer" as any,
}

const $serviceToggleContainer: ViewStyle = {
  paddingHorizontal: spacing.md,
  paddingVertical: spacing.xs,
  backgroundColor: colors.surface,
}

const $serviceToggle: ViewStyle = {
  flexDirection: "row",
  backgroundColor: colors.surfaceSecondary,
  borderRadius: 8,
  padding: 2,
}

const $serviceToggleButton: ViewStyle = {
  flex: 1,
  paddingVertical: 8,
  paddingHorizontal: 8,
  borderRadius: 6,
  alignItems: "center",
  cursor: "pointer" as any,
}

const $serviceToggleButtonActive: ViewStyle = {
  backgroundColor: colors.primary,
}

const $serviceToggleText: TextStyle = {
  fontSize: 12,
  fontWeight: "500",
  color: colors.palette.neutral600,
  textAlign: "center",
}

const $serviceToggleTextActive: TextStyle = {
  color: colors.surface,
  fontWeight: "600",
}

const $saturdayHint: ViewStyle = {
  flexDirection: "row",
  alignItems: "center",
  gap: 5,
  marginTop: 6,
  paddingLeft: 2,
}

const $saturdayHintText: TextStyle = {
  fontSize: 11,
  color: colors.modalSlateText,
  fontWeight: "500",
}

const $scrollViewFlex: ViewStyle = {
  flex: 1,
  minHeight: 0,
}

const $content: ViewStyle = {
  paddingHorizontal: spacing.xl,
  paddingVertical: spacing.lg,
  paddingRight: spacing.xl, // Fix right padding overflow
}

const _$formGroup: ViewStyle = {
  marginBottom: spacing.lg,
}

const _$label: TextStyle = {
  fontSize: 14,
  fontWeight: "600",
  color: colors.text,
  marginBottom: spacing.xs,
}

const _$inputContainer: ViewStyle = {
  flexDirection: "row",
  alignItems: "center",
  backgroundColor: colors.surface,
  borderWidth: 1,
  borderColor: colors.palette.neutral300,
  borderRadius: 8,
  paddingHorizontal: spacing.md,
  paddingVertical: spacing.sm,
  cursor: "pointer" as any,
  // @ts-ignore - Web-specific property
  transition: "all 0.2s ease",
}

const _$dateDisplayText: TextStyle = {
  fontSize: 16,
  color: colors.text,
  fontWeight: "500",
}

const _$hint: TextStyle = {
  fontSize: 12,
  color: colors.textDim,
  marginTop: spacing.xxs,
}

const _$checkboxRow: ViewStyle = {
  flexDirection: "row",
  alignItems: "center",
  marginBottom: spacing.lg,
  cursor: "pointer" as any,
}

const _$checkbox: ViewStyle = {
  width: 20,
  height: 20,
  borderWidth: 2,
  borderColor: colors.palette.neutral400,
  borderRadius: 4,
  marginRight: spacing.sm,
  justifyContent: "center",
  alignItems: "center",
  // @ts-ignore - Web-specific property
  transition: "all 0.2s ease",
}

const _$checkboxChecked: ViewStyle = {
  backgroundColor: colors.primary,
  borderColor: colors.primary,
}

const _$checkboxLabel: TextStyle = {
  fontSize: 14,
  color: colors.text,
  fontWeight: "500",
}

const _$errorContainer: ViewStyle = {
  flexDirection: "row",
  alignItems: "center",
  backgroundColor: colors.errorBackground || colors.errorBgLight,
  padding: spacing.sm,
  borderRadius: 8,
  marginBottom: spacing.md,
}

const _$errorText: TextStyle = {
  fontSize: 14,
  color: colors.surface,
  marginLeft: spacing.xs,
  flex: 1,
}

const _$submitButton: ViewStyle = {
  marginTop: spacing.md,
  backgroundColor: colors.primary,
  minHeight: 48,
}

// Support footer — compact single-line
const $supportFooterCompact: ViewStyle = {
  flexDirection: "row",
  alignItems: "center",
  justifyContent: "center",
  gap: 6,
  flexWrap: "wrap",
  paddingVertical: 10,
  paddingHorizontal: spacing.md,
  borderTopWidth: 1,
  borderTopColor: colors.palette.neutral200,
}

const $supportFooterLabel: TextStyle = {
  fontSize: 11,
  color: colors.palette.neutral450,
  fontWeight: "500",
}

const $supportFooterLink: ViewStyle = {
  flexDirection: "row",
  alignItems: "center",
  gap: 3,
  paddingVertical: 2,
}

const $supportFooterLinkText: TextStyle = {
  fontSize: 11,
  fontWeight: "600",
  color: colors.text,
}

const $supportFooterDivider: TextStyle = {
  fontSize: 11,
  color: colors.modalBorder,
}

// Company Selection Styles
const $searchFieldContainer: ViewStyle = {
  marginBottom: spacing.sm,
  marginTop: spacing.sm,
}

const $searchInputContainer: ViewStyle = {
  flexDirection: "row",
  alignItems: "center",
  backgroundColor: colors.surface,
  borderWidth: 1,
  borderColor: colors.border,
  borderRadius: 8,
  paddingHorizontal: spacing.md,
  paddingVertical: spacing.md,
  minHeight: 48,
}

const $searchInputText: TextStyle = {
  color: colors.palette.neutral900,
  fontSize: 15,
  fontWeight: "400",
}

const $searchIconAccessory: ViewStyle = {
  marginLeft: spacing.xs,
  justifyContent: "center",
  alignItems: "center",
}

const _$searchInput: TextStyle = {
  flex: 1,
  fontSize: 15,
  color: colors.text,
  marginLeft: spacing.xs,
  paddingVertical: 0,
  // @ts-ignore - Web-specific property to remove focus outline
  outlineStyle: "none",
}

const $clearButton: ViewStyle = {
  padding: 4,
  borderRadius: 12,
  backgroundColor: colors.footerBorder,
}

const $companyScrollView: ViewStyle = {
  maxHeight: 300,
  marginBottom: spacing.sm,
}

const $companyOption: ViewStyle = {
  flexDirection: "row",
  alignItems: "center",
  paddingVertical: spacing.xs,
  paddingHorizontal: spacing.sm,
  backgroundColor: colors.surface,
  borderRadius: 6,
  marginBottom: spacing.xxs,
  borderWidth: 1,
  borderColor: colors.border,
}

const $radioButton: ViewStyle = {
  width: 16,
  height: 16,
  borderRadius: 8,
  borderWidth: 2,
  borderColor: colors.primary,
  marginRight: spacing.xs,
  justifyContent: "center",
  alignItems: "center",
}

const $radioButtonSelected: ViewStyle = {
  width: 8,
  height: 8,
  borderRadius: 4,
  backgroundColor: colors.primary,
}

const $companyInfo: ViewStyle = {
  flex: 1,
}

const $companyName: TextStyle = {
  fontSize: 14,
  fontWeight: "600",
  color: colors.text,
  marginBottom: 2,
}

const $companyRegion: TextStyle = {
  fontSize: 12,
  fontWeight: "400",
  color: colors.textDim,
  marginTop: 2,
}

const _$companyHint: TextStyle = {
  fontSize: 13,
  color: colors.textDim,
  fontStyle: "italic",
  marginTop: spacing.xs,
}

const $noResultsContainer: ViewStyle = {
  alignItems: "center",
  paddingVertical: spacing.xl,
}

const $noResultsText: TextStyle = {
  fontSize: 14,
  color: colors.textDim,
  fontStyle: "italic",
  marginTop: spacing.sm,
}

const $loadingContainer: ViewStyle = {
  alignItems: "center",
  justifyContent: "center",
  paddingVertical: spacing.xl,
}

const $loadingText: TextStyle = {
  fontSize: 13,
  color: colors.textDim,
  fontWeight: "500",
}

// Progress Bar Styles
const $progressBarContainer: ViewStyle = {
  paddingHorizontal: spacing.lg,
  paddingVertical: spacing.md,
  borderBottomWidth: 1,
  borderBottomColor: colors.border || colors.palette.neutral200,
  backgroundColor: colors.surface,
}

const $progressBarWithBack: ViewStyle = {
  flexDirection: "row",
  alignItems: "center",
  justifyContent: "center",
  position: "relative",
}

const $backIconButton: ViewStyle = {
  position: "absolute",
  left: 0,
  padding: spacing.xs,
  borderRadius: 8,
}

const _$progressBarTitle: TextStyle = {
  fontSize: 13,
  fontWeight: "600",
  color: colors.textDim,
  marginBottom: spacing.sm,
  textAlign: "center",
}

const $progressBarTrack: ViewStyle = {
  flexDirection: "row",
  alignItems: "center",
  justifyContent: "center",
}

const $progressStepWrapper: ViewStyle = {
  flexDirection: "row",
  alignItems: "center",
}

const $progressStepIndicator: ViewStyle = {
  width: 32,
  height: 32,
  borderRadius: 16,
  backgroundColor: colors.modalBorder, // Gray for future steps
  alignItems: "center",
  justifyContent: "center",
  borderWidth: 2,
  borderColor: colors.modalBorder,
}

const $progressStepCompleted: ViewStyle = {
  backgroundColor: colors.text,
  borderColor: colors.text,
}

const $progressStepCurrent: ViewStyle = {
  backgroundColor: colors.primary, // Primary red
  borderColor: colors.primary,
}

const $progressStepNumber: TextStyle = {
  fontSize: 14,
  fontWeight: "600",
  color: colors.palette.neutral450,
}

const $progressStepNumberCurrent: TextStyle = {
  color: colors.surface,
}

const $progressConnector: ViewStyle = {
  width: 24,
  height: 2,
  backgroundColor: colors.modalBorder,
  marginHorizontal: spacing.xxs,
}

// Step Container Styles
const $stepContainer: ViewStyle = {
  paddingBottom: spacing.lg,
}

const $stepTitle: TextStyle = {
  fontSize: 18,
  fontWeight: "700",
  color: colors.text,
}

const _$calendarContainer: ViewStyle = {
  marginBottom: spacing.lg,
}

// Navigation Button Styles
const _$navigationButtons: ViewStyle = {
  flexDirection: "row",
  gap: spacing.sm,
  marginTop: spacing.lg,
}

const _$backButton: ViewStyle = {
  flex: 1,
}

const _$nextButton: ViewStyle = {
  flex: 1,
}

const _$bookButton: ViewStyle = {
  flex: 1,
}

// Summary Styles
const _$summaryCard: ViewStyle = {
  backgroundColor: colors.modalInputBg,
  borderRadius: 12,
  padding: spacing.md,
  marginBottom: spacing.lg,
  borderWidth: 1,
  borderColor: colors.modalBorder,
  marginTop: spacing.sm,
}

const _$summaryRow: ViewStyle = {
  flexDirection: "row",
  alignItems: "flex-start",
  marginBottom: spacing.md,
}

const _$summaryContent: ViewStyle = {
  flex: 1,
  marginLeft: spacing.sm,
}

const _$summaryLabel: TextStyle = {
  fontSize: 12,
  color: colors.textDim,
  marginBottom: spacing.xxs,
}

const _$summaryValue: TextStyle = {
  fontSize: 16,
  fontWeight: "600",
  color: colors.text,
}

const _$checkboxContainer: ViewStyle = {
  flexDirection: "row",
  alignItems: "center",
  marginBottom: spacing.lg,
  cursor: "pointer" as any,
}

const $numberPlateContainer: ViewStyle = {
  marginBottom: spacing.lg,
  alignItems: "center",
  justifyContent: "center",
  paddingVertical: spacing.sm,
  width: "100%",
  maxWidth: 300, // Reduced from 400 to fit modal better
  alignSelf: "center",
  height: 60, // Reduced from 80 to scale proportionally
}

// ── Redesigned Summary V2 Styles (matching OrbitControl) ──

const $summaryCardV2: ViewStyle = {
  borderRadius: 14,
  borderWidth: 1,
  borderColor: colors.modalBorderSubtle,
  backgroundColor: colors.modalInputBg,
  marginTop: spacing.sm,
  // Establish a stacking context so the inline service dropdown renders above
  // the Kennzeichen toggle and "Jetzt buchen" button below.
  position: "relative",
  zIndex: 100,
  // overflow intentionally left default so the service dropdown can overflow the card
}

const $terminCardInner: ViewStyle = {
  padding: 16,
}

const $terminHeaderRow: ViewStyle = {
  flexDirection: "row",
  alignItems: "center",
  justifyContent: "space-between",
  marginBottom: 10,
}

const $terminLabel: TextStyle = {
  fontSize: 11,
  fontWeight: "600",
  color: colors.palette.neutral450,
  letterSpacing: 0.6,
  textTransform: "uppercase",
}

const $terminEditLink: TextStyle = {
  fontSize: 12,
  fontWeight: "600",
  color: colors.primary,
}

const $terminPrimaryRow: ViewStyle = {
  flexDirection: "row",
  alignItems: "center",
  flexWrap: "wrap",
  gap: 6,
  // Lift above the date and location lines that come after it in the card.
  position: "relative",
  zIndex: 10,
}

const $serviceTriggerWrap: ViewStyle = {
  position: "relative",
  zIndex: 20,
}

const $serviceTrigger: ViewStyle = {
  flexDirection: "row",
  alignItems: "center",
  gap: 2,
  cursor: "pointer" as any,
  // expand tap area a little without shifting the baseline
  paddingVertical: 2,
  paddingHorizontal: 2,
  marginHorizontal: -2,
  borderRadius: 6,
}

const $serviceTriggerText: TextStyle = {
  fontSize: 15,
  fontWeight: "600",
  color: colors.text,
  lineHeight: 22,
}

const $terminDotSeparator: TextStyle = {
  fontSize: 15,
  color: colors.palette.neutral450,
  lineHeight: 22,
}

const $terminDeliveryText: TextStyle = {
  fontSize: 15,
  fontWeight: "600",
  color: colors.text,
  lineHeight: 22,
}

const $terminSecondaryLine: TextStyle = {
  fontSize: 14,
  fontWeight: "500",
  color: colors.palette.neutral600,
  lineHeight: 20,
  marginTop: 3,
}

const $terminLocationLine: TextStyle = {
  fontSize: 12,
  color: colors.palette.neutral450,
  lineHeight: 18,
  marginTop: 6,
}

const $serviceDropdownPanel: ViewStyle = {
  position: "absolute",
  top: "100%",
  left: 0,
  marginTop: 4,
  minWidth: 180,
  backgroundColor: colors.surface,
  borderWidth: 1,
  borderColor: colors.modalBorderSubtle,
  borderRadius: 10,
  boxShadow: "0 4px 16px rgba(0,0,0,0.1)" as any, // web-only shadow, ignored on native
  zIndex: 20,
  overflow: "hidden",
}

const $serviceDropdownItem: ViewStyle = {
  paddingVertical: 9,
  paddingHorizontal: 14,
  cursor: "pointer" as any,
}

const $serviceDropdownItemHover: ViewStyle = {
  backgroundColor: colors.modalInputBg,
}

const $serviceDropdownItemText: TextStyle = {
  fontSize: 13,
  fontWeight: "500",
  color: colors.text,
}

const $toggleOption: ViewStyle = {
  flexDirection: "row",
  alignItems: "center",
  gap: 12,
  paddingVertical: 10,
  paddingHorizontal: 14,
  borderRadius: 10,
  borderWidth: 1,
  borderColor: colors.modalBorderSubtle,
  backgroundColor: colors.surface,
  cursor: "pointer" as any,
}

const $toggleOptionActive: ViewStyle = {
  borderColor: "rgba(255, 56, 47, 0.25)",
  backgroundColor: "rgba(255, 56, 47, 0.03)",
}

const $toggleTrack: ViewStyle = {
  width: 38,
  height: 22,
  borderRadius: 11,
  backgroundColor: colors.palette.neutral300,
  justifyContent: "center",
}

const $toggleTrackActive: ViewStyle = {
  backgroundColor: colors.primary,
}

const $toggleKnob: ViewStyle = {
  width: 16,
  height: 16,
  borderRadius: 8,
  backgroundColor: colors.surface,
  position: "absolute",
  top: 3,
  left: 3,
  // @ts-ignore - Web shadow
  boxShadow: "0 1px 3px rgba(0,0,0,0.15)",
  elevation: 2,
}

const $toggleKnobActive: ViewStyle = {
  left: 19,
}

const $toggleLabel: TextStyle = {
  fontSize: 14,
  fontWeight: "500",
  color: colors.text,
}

const $toggleSublabel: TextStyle = {
  fontSize: 12,
  color: colors.modalSlateText,
  marginTop: 1,
}

const $errorContainerV2: ViewStyle = {
  flexDirection: "row",
  alignItems: "center",
  gap: 10,
  paddingVertical: 12,
  paddingHorizontal: 14,
  borderRadius: 10,
  backgroundColor: colors.errorBgLight,
  borderWidth: 1,
  borderColor: colors.errorBorderLight,
  marginTop: 20,
}

const $errorTextV2: TextStyle = {
  fontSize: 13,
  color: colors.primary,
  flex: 1,
  lineHeight: 18,
}

const $ctaButton: ViewStyle = {
  flexDirection: "row",
  alignItems: "center",
  justifyContent: "center",
  gap: 10,
  minHeight: 52,
  borderRadius: 12,
  backgroundColor: colors.primary,
  // @ts-ignore - Web shadow
  boxShadow: "0 2px 8px rgba(255, 56, 47, 0.15)",
  elevation: 3,
  cursor: "pointer" as any,
}

const $ctaButtonLoading: ViewStyle = {
  backgroundColor: colors.text,
}

const $ctaButtonPressed: ViewStyle = {
  backgroundColor: colors.primary,
  // @ts-ignore - Web shadow
  boxShadow: "0 4px 16px rgba(255, 56, 47, 0.3)",
  transform: [{ translateY: -1 }],
}

const $ctaButtonText: TextStyle = {
  fontSize: 15,
  fontWeight: "600",
  color: colors.surface,
  letterSpacing: 0.2,
}

// ── Document upload styles (PLA-122) ──────────────────────────────────────────

// Drop-zone visual — mirrors OrbitControl's ConfirmStep.DocumentPickerPanel.
// Vertical stack of icon + primary text + hint, with dashed border on a tinted
// surface; the invisible <input type="file"> overlay handles click selection.
const $documentDropZone: ViewStyle = {
  flexDirection: "column",
  alignItems: "center",
  justifyContent: "center",
  gap: 6,
  paddingVertical: 16,
  paddingHorizontal: 12,
  borderRadius: 10,
  borderWidth: 2,
  borderStyle: "dashed",
  borderColor: "#D5D9E0",
  backgroundColor: "#FAFBFC",
  cursor: "pointer" as any,
  position: "relative",
  overflow: "hidden",
}

const $documentDropZonePrimary: TextStyle = {
  fontSize: 13,
  color: "#6B7280",
  fontWeight: "500",
  textAlign: "center",
}

const $documentDropZoneHint: TextStyle = {
  fontSize: 11,
  color: "#9CA3AF",
  textAlign: "center",
}

const $documentFileRow: ViewStyle = {
  flexDirection: "row",
  alignItems: "center",
  gap: 8,
  paddingVertical: 8,
  paddingHorizontal: 12,
  borderRadius: 8,
  borderWidth: 1,
  borderColor: colors.modalBorderSubtle,
  backgroundColor: colors.surface,
}

const $documentFileName: TextStyle = {
  fontSize: 13,
  color: colors.text,
  fontWeight: "500",
}

const $documentFileSize: TextStyle = {
  fontSize: 11,
  color: colors.modalSlateText,
  marginTop: 1,
}

const $retryBannerContainer: ViewStyle = {
  flexDirection: "row",
  alignItems: "center",
  flexWrap: "wrap",
  gap: 6,
  paddingVertical: 12,
  paddingHorizontal: 14,
  borderRadius: 10,
  backgroundColor: "rgba(34, 134, 58, 0.06)",
  borderWidth: 1,
  borderColor: "rgba(34, 134, 58, 0.25)",
  marginTop: 12,
}

const $retryBannerText: TextStyle = {
  fontSize: 13,
  color: colors.text,
  flex: 1,
  lineHeight: 18,
}

const $retryBannerLink: TextStyle = {
  fontSize: 13,
  color: colors.primary,
  fontWeight: "600",
  textDecorationLine: "underline",
}

// Shared label container used by all toggle options (Kennzeichen, Dokumente)
const $toggleLabelContainer: ViewStyle = {
  flex: 1,
}

const $documentSection: ViewStyle = {
  gap: 8,
}

const $documentFileInfo: ViewStyle = {
  flex: 1,
}

const $documentFileRemoveButton: ViewStyle = {
  padding: 4,
}

// Plain JS object for the web-only native <input> element overlay (not a RN ViewStyle)
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const webFileInputStyle: any = {
  position: "absolute",
  top: 0,
  left: 0,
  width: "100%",
  height: "100%",
  opacity: 0,
  cursor: "pointer",
}

// ── Nachricht styles (PLA-151) ─────────────────────────────────────────────────
// Outer card matches the Dokumente drop-zone visual: dashed border, soft tint,
// same padding/radius — so the two heavier optional inputs look like siblings.
const $nachrichtSection: ViewStyle = {
  gap: 6,
  paddingVertical: 16,
  paddingHorizontal: 12,
  borderRadius: 10,
  borderWidth: 2,
  borderStyle: "dashed",
  borderColor: "#D5D9E0",
  backgroundColor: "#FAFBFC",
}

// Plain TextInput — no inner border; the card provides the visual container.
const $nachrichtInput: TextStyle = {
  fontSize: 14,
  color: "#111E2C",
  minHeight: 72, // ~3 rows at default line height
  paddingTop: 0,
  paddingBottom: 0,
  // @ts-ignore - Web-specific: remove browser focus ring
  outlineStyle: "none",
}

const $nachrichtCounter: TextStyle = {
  fontSize: 11,
  color: "#9CA3AF",
  textAlign: "right",
  marginTop: 2,
}
